import React, { useState, useEffect, useRef } from "react";
import MyRouter from "routers/index";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Toaster } from "react-hot-toast";
import { WishlistProvider } from "contexts/WishlistContext";
import * as Sentry from "@sentry/react";
import type { ChatWidgetLiteHandle } from "widgets/ChatWidgetLite";

function App() {
  const [chatOpen, setChatOpen] = useState(false);
  const [ChatWidgetComponent, setChatWidgetComponent] = useState<React.ComponentType<any> | null>(null);
  const chatWidgetRef = useRef<ChatWidgetLiteHandle>(null);
  const chatTriggerRef = useRef<HTMLButtonElement>(null);
  const chatCloseRef = useRef<HTMLButtonElement>(null);
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    if (typeof window !== 'undefined') {
      // Priority 1: User's explicit choice
      if (localStorage.theme === 'dark') {
        document.documentElement.classList.add('dark');
        return 'dark';
      }
      
      if (localStorage.theme === 'light') {
        document.documentElement.classList.remove('dark');
        return 'light';
      }

      // Priority 2: System preference (ONLY if no manual choice exists)
      document.documentElement.classList.remove('dark');
      return 'light';
    }
    return 'light';
  });

  // Sync theme state with document class changes
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const isDark = document.documentElement.classList.contains('dark');
          setTheme(isDark ? 'dark' : 'light');
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  // ESC key to close chat
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setChatOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Focus management for the chat modal (manual a11y checklist).
  useEffect(() => {
    if (chatOpen) {
      // Move focus into the dialog.
      const id = window.setTimeout(() => chatCloseRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    // Restore focus to trigger when closing.
    chatTriggerRef.current?.focus();
  }, [chatOpen]);

  useEffect(() => {
    if (!chatOpen || ChatWidgetComponent) return;

    let cancelled = false;

    import("widgets/ChatWidgetLite")
      .then((module) => {
        if (!cancelled) {
          setChatWidgetComponent(() => module.default);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setChatWidgetComponent(() => null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [ChatWidgetComponent, chatOpen]);

  const handleClearChat = () => {
    if (window.confirm("Are you sure you want to clear the chat history?")) {
      chatWidgetRef.current?.clearChat();
    }
  };

  return (
    <HelmetProvider>
      <WishlistProvider>
        <Helmet>
          <title>iForge || Online Store</title>
          <meta
            name="description"
            content="Shop iForge Online Store for 3D printers, filaments, robotics gear, accessories, and spare parts with support for makers, classrooms, and engineering teams."
          />
        </Helmet>

        {/* Toast notifications */}
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 4000,
            style: {
              fontSize: "14px",
              padding: "12px 16px",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
            },
            className:
              "bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600",
            success: { style: { borderLeft: "4px solid #22c55e" } },
            error: { style: { borderLeft: "4px solid #ef4444" } },
          }}
        />

        {/* MAIN APP */}
        <div className="bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-200 font-sans">
          <MyRouter />
        </div>

        {/* Polished Floating Chat Button */}
        <button
          ref={chatTriggerRef}
          type="button"
          aria-label="Open iForge Assistant"
          className="chat-fab"
          onClick={() => setChatOpen(true)}
          title="Chat with iForge Assistant"
        >
          {/* Chat Bubble with Dots Icon (Same as Landing Page) */}
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM8 11C7.45 11 7 10.55 7 10C7 9.45 7.45 9 8 9C8.55 9 9 9.45 9 10C9 10.55 8.55 11 8 11ZM12 11C11.45 11 11 10.55 11 10C11 9.45 11.45 9 12 9C12.55 9 13 9.45 13 10C13 10.55 12.55 11 12 11ZM16 11C15.45 11 15 10.55 15 10C15 9.45 15.45 9 16 9C16.55 9 17 9.45 17 10C17 10.55 16.55 11 16 11Z" />
          </svg>
        </button>

        {/* Polished Chat Modal */}
        {chatOpen && (
          <>
            <div
              className="chat-overlay"
              onClick={() => setChatOpen(false)}
            />
            <div
              className="chat-modal"
              role="dialog"
              aria-modal="true"
              aria-label="iForge Assistant"
            >
              <div className="chat-modal__header">
                <div className="chat-modal__title">
                  <svg viewBox="0 0 25 25" fill="currentColor">
                    <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4v3c0 .6.4 1 1 1 .2 0 .5-.1.7-.3L14.4 18H20c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM8 14H6v-2h2v2zm0-3H6V9h2v2zm0-3H6V6h2v2zm7 6h-5v-2h5v2zm3-3h-8V9h8v2zm0-3h-8V6h8v2z"/>
                  </svg>
                  iForge Assistant
                </div>
                <div className="chat-modal__actions">
                  <button 
                    className="chat-modal__clear" 
                    onClick={handleClearChat}
                    title="Clear Chat History"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                    </svg>
                  </button>
                  <button
                    ref={chatCloseRef}
                    className="chat-modal__close"
                    onClick={() => setChatOpen(false)}
                    aria-label="Close chat"
                  >
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                </div>
              </div>
              <div className="chat-modal__body">
                {ChatWidgetComponent ? (
                  <ChatWidgetComponent
                    ref={chatWidgetRef}
                    apiBase="https://iforgebot.codesbynewton.com"
                    theme={theme}
                  />
                ) : (
                  <div className="flex h-full items-center justify-center p-6 text-sm text-slate-500 dark:text-slate-400">
                    Loading assistant...
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </WishlistProvider>
    </HelmetProvider>
  );
}

export default Sentry.withProfiler(App);
